import { computed, reactive, ref, watch, type Ref } from 'vue';
import useVuelidate from '@vuelidate/core';
import { createI18nMessage, required, requiredIf } from '@vuelidate/validators';

import type { useUserStore } from '@/stores/user';
import type { UserDashboardLeaveCampaignHelperRequestType } from '@magma-app/magma-types/all';

import AnalyticsHelper from '@/helpers/analytics';

import type {
  SlideOverDeactivationForm,
  SlideOverOptOutReason,
  SlideOverReactivationIn,
  SlideOverSelectOption,
} from '../types';

type UserStore = ReturnType<typeof useUserStore>;

export function useSlideOverDeactivation(params: {
  campaignId: Ref<number | null | undefined>;
  deactivateCampaignUnstartedRelations: Ref<number | undefined>;
  userStore: UserStore;
  t: (key: string) => string;
  onSuccess: () => void;
}) {
  const withI18nMessage = createI18nMessage({ t: params.t });

  const form = reactive<SlideOverDeactivationForm>({
    keepUnstartedRelations: null,
    optOutReason: '',
    reactivationIn: null,
  });

  const state = reactive({
    deactivation: form,
  });

  const v$ = useVuelidate(
    {
      deactivation: {
        keepUnstartedRelations: {
          requiredIfRef: withI18nMessage(
            requiredIf(
              () => (params.deactivateCampaignUnstartedRelations.value ?? 0) > 0
            ),
            {
              messagePath: () => params.t('validation.value'),
            }
          ),
        },
        optOutReason: {
          required: withI18nMessage(required, {
            messagePath: () => params.t('validation.value'),
          }),
        },
        reactivationIn: {
          required: withI18nMessage(required, {
            messagePath: () => params.t('validation.value'),
          }),
        },
      },
    },
    state
  );

  // Keep the same eager validation behavior as the previous implementation.
  watch(
    () => form,
    async () => {
      await v$.value.deactivation.$validate();
    },
    { deep: true }
  );

  const options = computed(() => {
    const keepUnstartedRelations: SlideOverSelectOption<boolean>[] = [
      {
        label: params.t(
          'slideOverModal.deactivate.keepUnstartedRelations.true'
        ),
        value: true,
      },
      {
        label: params.t(
          'slideOverModal.deactivate.keepUnstartedRelations.false'
        ),
        value: false,
      },
    ];

    const optOutReason: SlideOverSelectOption<SlideOverOptOutReason>[] = [
      {
        label: params.t(
          'slideOverModal.deactivate.optOutReason.tooManyRelations'
        ),
        value: 'too-many-relations',
      },
      {
        label: params.t('slideOverModal.deactivate.optOutReason.noMoreTime'),
        value: 'no-more-time',
      },
      {
        label: params.t('slideOverModal.deactivate.optOutReason.doesntWant'),
        value: 'doesnt-want',
      },
      {
        label: params.t('slideOverModal.deactivate.optOutReason.noMoreInOrg'),
        value: 'no-more-in-org',
      },
    ];

    const reactivationIn: SlideOverSelectOption<SlideOverReactivationIn>[] = [
      {
        label: params.t('slideOverModal.deactivate.reactivationIn.next'),
        value: 1,
      },
      {
        label: params.t('slideOverModal.deactivate.reactivationIn.three'),
        value: 3,
      },
      {
        label: params.t('slideOverModal.deactivate.reactivationIn.six'),
        value: 6,
      },
      {
        label: params.t('slideOverModal.deactivate.reactivationIn.never'),
        value: 0,
      },
    ];

    return {
      keepUnstartedRelations,
      optOutReason,
      reactivationIn,
    };
  });

  const isSubmitting = ref(false);

  const optOutReasonEvents: Record<SlideOverOptOutReason, string> = {
    'too-many-relations': 'hs_deactivate_toorelations',
    'no-more-time': 'hs_deactivate_nottime',
    'doesnt-want': 'hs_deactivate_dontwant',
    'no-more-in-org': 'hs_deactivate_quitorga',
  };

  const reactivationInEvents: Record<SlideOverReactivationIn, string> = {
    0: 'hs_deactivate_never',
    1: 'hs_deactivate_nextmonth',
    3: 'hs_deactivate_threemonths',
    6: 'hs_deactivate_sixmonths',
  };

  const submitDeactivation = async () => {
    const campaignId = params.campaignId.value;
    if (!campaignId || isSubmitting.value) {
      return;
    }

    isSubmitting.value = true;

    try {
      const isValid = await v$.value.deactivation.$validate();
      if (!isValid || !form.optOutReason || form.reactivationIn === null) {
        return;
      }

      // The backend accepts 0 ("never") but the generated type only allows 1|3|6.
      // We keep the correct runtime value and cast at the call site.
      type LeavePayloadWithNever = Omit<
        UserDashboardLeaveCampaignHelperRequestType,
        'reactivationIn'
      > & {
        reactivationIn?: SlideOverReactivationIn;
      };

      const payload: LeavePayloadWithNever = {
        campaignId,
        optOutReason: form.optOutReason,
      };

      // Keep valid falsy values (false and 0) in the payload.
      if (form.keepUnstartedRelations !== null) {
        payload.keepUnstartedRelations = form.keepUnstartedRelations;
      }

      if (form.reactivationIn !== null) {
        payload.reactivationIn = form.reactivationIn;
      }

      await params.userStore.leaveCampaign(
        payload as UserDashboardLeaveCampaignHelperRequestType
      );

      const optOutReasonEventName = optOutReasonEvents[form.optOutReason];
      if (optOutReasonEventName) {
        AnalyticsHelper.trackEvent(optOutReasonEventName, {
          user_properties: 'helper',
        });
      }

      const reactivationEventName = reactivationInEvents[form.reactivationIn];
      if (reactivationEventName) {
        AnalyticsHelper.trackEvent(reactivationEventName, {
          user_properties: 'helper',
        });
      }

      AnalyticsHelper.trackEvent('hs_deactivate_confirmation', {
        user_properties: 'helper',
      });

      params.onSuccess();
    } finally {
      isSubmitting.value = false;
    }
  };

  const resetDeactivation = () => {
    form.keepUnstartedRelations = null;
    form.optOutReason = '';
    form.reactivationIn = null;
    v$.value.$reset();
  };

  return {
    deactivationForm: form,
    deactivationV$: v$,
    deactivationOptions: options,
    isDeactivationSubmitting: isSubmitting,
    submitDeactivation,
    resetDeactivation,
  };
}
